/** Хранилище настроек игрока.
 *
 * Внутри Telegram используется `CloudStorage`: настройки переезжают вместе
 * с аккаунтом на другое устройство, что для игры, в которую играют и с
 * телефона, и с планшета, важнее скорости чтения.
 *
 * Снаружи (и на случай, если CloudStorage откажет) остаётся `localStorage`.
 * Пишем всегда в оба: облако может быть недоступно в момент сохранения, и
 * терять из-за этого настройки не хочется.
 */

interface CloudStorage {
  getItem(key: string, callback: (error: string | null, value: string | null) => void): void
  setItem(
    key: string,
    value: string,
    callback?: (error: string | null, saved: boolean) => void,
  ): void
}

function cloud(): CloudStorage | undefined {
  return (window.Telegram?.WebApp as unknown as { CloudStorage?: CloudStorage })
    ?.CloudStorage
}

export async function readSetting(key: string): Promise<string | null> {
  const local = localStorage.getItem(key)
  const storage = cloud()
  if (!storage) return local

  return new Promise((resolve) => {
    let settled = false
    const finish = (value: string | null) => {
      if (settled) return
      settled = true
      resolve(value)
    }

    // Облако может молчать сколь угодно долго, а экран нужен сейчас:
    // не дождавшись ответа, читаем локальную копию.
    const timer = window.setTimeout(() => finish(local), 1000)
    try {
      storage.getItem(key, (error, value) => {
        window.clearTimeout(timer)
        finish(error ? local : (value ?? local))
      })
    } catch {
      // Объект CloudStorage существует всегда, но вне Telegram и в старых
      // клиентах вызов бросает WebAppMethodUnsupported. Настройки не та
      // вещь, ради которой стоит ронять приложение.
      window.clearTimeout(timer)
      finish(local)
    }
  })
}

export function writeSetting(key: string, value: string): void {
  localStorage.setItem(key, value)
  try {
    cloud()?.setItem(key, value)
  } catch {
    // Локальная копия уже сохранена — этого достаточно.
  }
}
